export type RecoveryState = Readonly<{
    phase: 'entry' | 'proof' | 'pending' | 'completed';
    busy: boolean;
    code?: string;
    expiresAt?: string;
}>;
type Begin = {
    idempotencyKey: string;
    expectedVersion: null;
    input: {
        email: string;
        locale: string;
        nonce: string;
    };
};
type Finish = {
    idempotencyKey: string;
    expectedVersion: 1;
    input: {
        challengeId: string;
        nonce: string;
        proof: string;
        email: string;
        password: string;
    };
};
const random = () => Array.from(crypto.getRandomValues(new Uint8Array(32)), n => n.toString(16).padStart(2, '0')).join('');
const object = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const exact = (v: unknown, keys: string[]) => object(v) && Object.keys(v).sort().join() === [...keys].sort().join();
/** Anonymous purpose-only protocol. No bearer token, storage, credentials, auto
 * send/retry or session adoption. Unknown finish retains the SAME immutable key
 * and in-memory password until explicit check/resume/disposal. */
export function createAccountRecoveryClient(fetcher: typeof fetch = fetch) {
    let state: RecoveryState = { phase: 'entry', busy: false }, begin: Begin | undefined, finish: Finish | undefined, challenge: {
        id: string;
        expiresAt: string;
    } | undefined, dead = false, generation = 0, abort: AbortController | undefined;
    const path = typeof window === 'undefined' ? undefined : window.location.pathname + window.location.search;
    const current = (g: number) => !dead && generation === g && (path === undefined || window.location.pathname + window.location.search === path);
    const snapshot = () => Object.freeze({ ...state });
    async function post(part: string, body: unknown) { const r = await fetcher('/api/thistleglass/account/password/recovery/' + part, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body), credentials: 'omit', cache: 'no-store', redirect: 'error', referrerPolicy: 'no-referrer', signal: abort?.signal }); let value: unknown; try {
        value = await r.json();
    }
    catch { } return { status: r.status, value }; }
    function error(v: unknown) { return object(v) && typeof v.code === 'string' && /^[A-Z_]{1,80}$/.test(v.code) ? v.code : 'RECOVERY_STATUS_UNKNOWN'; }
    function complete() { begin = undefined; finish = undefined; challenge = undefined; state = { phase: 'completed', busy: true }; }
    async function work(fn: (g: number) => Promise<void>) { if (dead || state.busy)
        throw Error('RECOVERY_REQUEST_IN_PROGRESS'); const g = generation; abort = new AbortController(); state = { ...state, busy: true, code: undefined }; try {
        await fn(g);
    }
    catch {
        if (current(g))
            state = { ...state, phase: 'pending', code: 'RECOVERY_STATUS_UNKNOWN' };
    }
    finally {
        if (current(g)) {
            state = { ...state, busy: false };
            abort = undefined;
        }
    } return snapshot(); }
    async function sendBegin(g: number) { if (!begin)
        throw Error('NO_RECOVERY_REQUEST'); const r = await post('begin', begin); if (!current(g))
        return; const v = r.value; if (r.status === 202 && exact(v, ['challengeId', 'version', 'expiresAt', 'state', 'code', 'retryable']) && object(v) && typeof v.challengeId === 'string' && /^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/.test(v.challengeId) && v.version === 1 && v.state === 'pending' && v.code === 'RECOVERY_CHECK_MAILBOX' && v.retryable === false && typeof v.expiresAt === 'string' && Number.isFinite(Date.parse(v.expiresAt)) && new Date(v.expiresAt).toISOString() === v.expiresAt) {
        challenge = { id: v.challengeId, expiresAt: v.expiresAt };
        state = { phase: 'proof', busy: true, expiresAt: v.expiresAt };
        return;
    } const code = error(v), beforeIO = (r.status === 400 && code === 'INVALID_RECOVERY_INPUT') || (r.status === 403 && code === 'ORIGIN_DENIED') || (r.status === 503 && code === 'RECOVERY_DELIVERY_UNCONFIGURED') || (r.status === 429 && code === 'RECOVERY_RATE_LIMITED'); if (beforeIO)
        begin = undefined; state = { ...state, phase: beforeIO ? 'entry' : 'pending', code }; }
    async function sendFinish(g: number) { if (!finish)
        throw Error('NO_RECOVERY_REQUEST'); const r = await post('finish', finish); if (!current(g))
        return; const v = r.value; if (r.status === 200 && exact(v, ['code', 'state', 'retryable']) && object(v) && v.code === 'RECOVERY_REQUEST_FINISHED' && v.state === 'completed' && v.retryable === false) {
        complete();
        return;
    } const code = error(v); if (r.status === 403 && code === 'RECOVERY_PROOF_DENIED' && exact(v, ['code', 'retryable']) && object(v) && v.retryable === false) {
        finish = undefined;
        state = { ...state, phase: 'proof', code };
    }
    else
        state = { ...state, phase: 'pending', code }; }
    return Object.freeze({ snapshot,
        begin(email: string, locale: string) { return work(async (g) => { if (begin || finish || challenge)
            throw Error('EXISTING_RECOVERY_REQUEST'); begin = { idempotencyKey: random(), expectedVersion: null, input: { email, locale: ['en', 'es', 'fr', 'de', 'ja'].includes(locale) ? locale : 'en', nonce: random() } }; await sendBegin(g); }); },
        finish(proof: string, password: string) { return work(async (g) => { if (!challenge || !begin || finish)
            throw Error('EXISTING_RECOVERY_REQUEST'); if (Date.parse(challenge.expiresAt) <= Date.now()) {
            state = { ...state, phase: 'proof', code: 'RECOVERY_PROOF_EXPIRED' };
            return;
        } finish = { idempotencyKey: random(), expectedVersion: 1, input: { challengeId: challenge.id, nonce: begin.input.nonce, email: begin.input.email, proof, password } }; await sendFinish(g); }); },
        check() { return work(async (g) => { if (!challenge || !begin) {
            await sendBegin(g);
            return;
        } const r = await post('status', { challengeId: challenge.id, nonce: begin.input.nonce }); if (!current(g))
            return; const v = r.value; if (r.status === 200 && exact(v, ['state', 'code', 'retryable']) && object(v) && v.state === 'completed' && v.code === 'RECOVERY_REQUEST_FINISHED' && v.retryable === false) {
            complete();
            return;
        } if (r.status === 200 && exact(v, ['state', 'retryable']) && object(v) && v.state === 'pending' && v.retryable === false) {
            state = { ...state, phase: finish ? 'pending' : 'proof' };
            return;
        } state = { ...state, phase: 'pending', code: error(v) }; }); },
        resume() { return work(async (g) => { if (finish)
            await sendFinish(g);
        else
            await sendBegin(g); }); },
        restartExpired() { if (dead || state.busy || finish || !challenge || Date.parse(challenge.expiresAt) > Date.now())
            throw Error('RECOVERY_NOT_EXPIRED_OR_UNRESOLVED'); begin = undefined; challenge = undefined; state = { phase: 'entry', busy: false }; return snapshot(); },
        dispose() { dead = true; generation++; abort?.abort(); abort = undefined; begin = undefined; finish = undefined; challenge = undefined; state = { phase: 'entry', busy: false }; }
    });
}
